import { useMemo, useState } from 'react';

import {
  U21Button,
  U21ButtonGroup,
  U21Chip,
  U21LoadError,
  U21Loading,
  U21NoValue,
  U21Spacer,
  U21Timeline,
  U21TimelineEntry,
  U21TimelineEntryColor,
  U21Typography,
} from 'app/shared/u21-ui/components';
import {
  IconArrowDown,
  IconArrowRight,
  IconArrowUp,
  IconPencil,
} from '@u21/tabler-icons';
import { EntityRiskScoreDisplay } from 'app/modules/riskScore/components/EntityRiskScore';
import { getRiskCategoryStyles } from 'app/modules/riskRatings/helpers';
import {
  HistoricRiskScoreRow,
  useGetHistoricRiskScores,
} from 'app/modules/riskScore/queries/useGetHistoricRiskScores';
import { filterUnchangedRiskScores } from 'app/modules/riskScore/utils';
import { ROUTES_MAP } from 'app/shared/utils/routes';
import { ENTITY_DETAILS_DEFAULT_TABS } from 'app/modules/entities/constants';
import { useFetchEntityRiskScore } from 'app/modules/riskScore/queries/useFetchEntityRiskScore';
import styled from 'styled-components';
import { RiskScoreView } from 'app/modules/riskScore/models';

const SIDEBAR_RISK_SCORE_LIMIT = 10;

interface OwnProps {
  entityExternalId: string;
  entityId: number | string;
}

export const SidebarRiskScoreTimeline = ({
  entityExternalId,
  entityId,
}: OwnProps) => {
  const {
    data: historicRiskScores,
    isError: isHistoricRiskScoreError,
    isLoading: isHistoricRiskScoreLoading,
    refetch: refetchHistoricRiskScore,
  } = useGetHistoricRiskScores(entityExternalId);
  const { data: latestRiskScore } = useFetchEntityRiskScore(entityExternalId);

  const [view, setView] = useState<RiskScoreView>('NET');

  const { rows, hiddenCount } = useMemo(() => {
    const results =
      historicRiskScores?.status === 'SUCCEEDED'
        ? historicRiskScores.results
        : [];
    const filtered =
      view === 'NET' ? filterUnchangedRiskScores(results) : results;
    // Only show a handful in the sidebar regardless of view; the full history
    // lives on the risk scores tab.
    return {
      rows: filtered.slice(0, SIDEBAR_RISK_SCORE_LIMIT),
      hiddenCount: Math.max(filtered.length - SIDEBAR_RISK_SCORE_LIMIT, 0),
    };
  }, [historicRiskScores, view]);

  const isErrorState =
    isHistoricRiskScoreError || historicRiskScores?.status === 'FAILED';
  const loading =
    isHistoricRiskScoreLoading || historicRiskScores?.status === 'PENDING';

  if (isErrorState) {
    return (
      <U21LoadError
        label="risk score history"
        onTryAgain={() => refetchHistoricRiskScore()}
      />
    );
  }
  if (loading) {
    return <U21Loading loading />;
  }

  return (
    <U21Spacer spacing={2}>
      <Option horizontal useFlexGap>
        <U21Typography variant="subtitle1">Score History</U21Typography>
        <U21ButtonGroup<RiskScoreView>
          buttons={[
            { label: 'Net Changes', value: 'NET' },
            { label: 'All Runs', value: 'ALL' },
          ]}
          size="small"
          value={view}
          onClick={(i) => setView(i)}
        />
      </Option>
      {!rows.length ? (
        <U21Typography variant="caption" color="text.secondary">
          No risk scores recorded for this entity yet.
        </U21Typography>
      ) : (
        <U21Timeline>
          {rows.map((i, j) => (
            <RiskScoreTimelineEntry
              key={i.id}
              delta={
                rows[j + 1] !== undefined
                  ? Math.round(i.score) - Math.round(rows[j + 1].score)
                  : undefined
              }
              isCurrent={j === 0 && Boolean(latestRiskScore?.result)}
              row={i}
            />
          ))}
          {hiddenCount > 0 && (
            <U21TimelineEntry color="default">
              <U21Typography variant="body2" color="text.secondary">
                {`... and ${hiddenCount} more`}
              </U21Typography>
            </U21TimelineEntry>
          )}
        </U21Timeline>
      )}
      <div>
        <U21Button
          color="primary"
          endIcon={<IconArrowRight />}
          to={ROUTES_MAP.entitiesIdTab.path
            .replace(':id', String(entityId))
            .replace(':tab?', ENTITY_DETAILS_DEFAULT_TABS['risk-scores'].key)}
          variant="text"
        >
          <U21Typography color="inherit" variant="subtitle2">
            View Full Score History
          </U21Typography>
        </U21Button>
      </div>
    </U21Spacer>
  );
};

// Node ring matches the score's risk category; overrides are info-colored.
// Falls back to neutral grey when there's no risk model to categorize against.
const getNodeColor = (
  row: HistoricRiskScoreRow,
  isOverride: boolean,
): U21TimelineEntryColor => {
  if (isOverride) {
    return 'info';
  }
  if (!row.risk_model) {
    return 'default';
  }
  return getRiskCategoryStyles(
    row.score,
    row.risk_model.high_risk_floor,
    row.risk_model.low_risk_ceiling,
  ).colorSchema;
};

const RiskScoreTimelineEntry = ({
  row,
  isCurrent,
  delta,
}: {
  row: HistoricRiskScoreRow;
  isCurrent: boolean;
  delta: number | undefined;
}) => {
  const isOverride = row.source === 'MANUAL';
  // Up = score increased = higher risk (error); down = decreased (success).
  const deltaUp = delta !== undefined && delta > 0;
  const nodeColor = getNodeColor(row, isOverride);

  return (
    <U21TimelineEntry
      color={nodeColor}
      icon={isOverride ? <IconPencil /> : undefined}
      timestamp={row.created_at}
    >
      <U21Spacer horizontal align="center" wrap>
        {row.risk_model ? (
          <EntityRiskScoreDisplay
            disablePopover={isOverride}
            latestRiskScore={{
              source: 'RISK_MODEL',
              created_at: row.created_at,
              updated_at: row.created_at,
              entity_external_id: row.entity_id,
              risk_model_id: row.risk_model_id,
              score: row.score,
              snapshot: row.snapshot ?? { trace: [] },
            }}
            readOnly
            riskModelSummary={row.risk_model}
            tooltip={isOverride ? 'Manual override' : undefined}
          />
        ) : (
          <U21NoValue />
        )}
        {delta !== undefined && delta !== 0 && (
          <U21Typography
            variant="subtitle2"
            color={deltaUp ? 'error' : 'success'}
            icon={deltaUp ? <IconArrowUp /> : <IconArrowDown />}
            spacingProps={{ spacing: 0 }}
          >
            {Math.abs(delta)}
          </U21Typography>
        )}
        {isCurrent && (
          <U21Chip color="primary" variant="ghost">
            Current
          </U21Chip>
        )}
      </U21Spacer>
    </U21TimelineEntry>
  );
};

const Option = styled(U21Spacer)`
  justify-content: space-between;
`;
